<template>
  <div id="signup-view">
    <b-overlay>
      <section class="bg-light ftco-section">
        <div class="d-flex justify-content-center">
          <img
            src="@/assets/site/images/loader.gif"
            v-if="loader"
            class="img-fluid loader-width"
            alt
          />
        </div>
        <div class="container" v-if="!loader">
          <div class="row flex-lg-row flex-md-row">
            <div class="col-md-5">
              <div style="margin-top: 2rem">
                <div class="card-body bg-dark-gray">
                  <h2 class="text-black mb-4">Sign up</h2>
                  <form>
                    <div class="form-group">
                      <input
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': fieldErrors.username }"
                        v-model="username"
                        placeholder="User Name*"
                      />
                      <span class="text-danger" v-if="fieldErrors.username">{{
                        fieldErrors.username[0]
                      }}</span>
                    </div>
                    <div class="form-group">
                      <input
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': fieldErrors.name }"
                        v-model="name"
                        placeholder="Name*"
                      />
                      <span class="text-danger" v-if="fieldErrors.name">{{
                        fieldErrors.name[0]
                      }}</span>
                    </div>

                    <div class="form-group">
                      <input
                        type="email"
                        class="form-control"
                        :class="{ 'is-invalid': fieldErrors.email }"
                        v-model="email"
                        placeholder="Email Address*"
                        autocomplete="email"
                        @input="onEmailFieldInput"
                        @blur="onEmailFieldBlur"
                      />
                      <span class="text-danger" v-if="fieldErrors.email">{{
                        fieldErrors.email[0]
                      }}</span>
                      <span
                        v-else-if="emailDomainStatus === 'checking'"
                        class="text-muted d-block mt-1 signup-field-hint"
                      >
                        We're verifying your email address...
                      </span>
                    </div>
                    <div class="form-group">
                      <div class="row">
                        <div class="col-md-2 pr-0">
                          <vue-country-code
                            @onSelect="onSelect"
                            :defaultCountry="country"
                            disabledFetchingCountry
                            enabledCountryCode
                            enabledFlags
                          >
                          </vue-country-code>
                        </div>
                        <div class="col-md-10 pl-0">
                          <input
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': fieldErrors.mobile }"
                            v-model="mobile"
                            placeholder="Phone Number*"
                            inputmode="numeric"
                          />
                        </div>
                      </div>
                      <span class="text-danger" v-if="fieldErrors.mobile">{{
                        fieldErrors.mobile[0]
                      }}</span>
                    </div>
                    <div class="form-group">
                      <b-form-group class="font-weight-600">
                        <label class="font-weight-600 mb-3"
                          >Gender <span class="text-danger">*</span></label
                        >
                        <div class="row pl-3">
                          <div class="col-md-3">
                            <b-form-radio
                              size="lg"
                              v-model="gender"
                              name="male"
                              value="Male"
                              class="radio-control"
                              >Male</b-form-radio
                            >
                          </div>
                          <div class="col-md-3">
                            <b-form-radio
                              size="lg"
                              v-model="gender"
                              name="female"
                              value="Female"
                              class="radio-control"
                              >Female</b-form-radio
                            >
                          </div>
                        </div>
                        <span class="text-danger d-block mt-2" v-if="fieldErrors.gender">{{
                          fieldErrors.gender[0]
                        }}</span>
                      </b-form-group>
                    </div>
                    <div class="form-group mt-3">
                      <input
                        type="password"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            fieldErrors.password || fieldErrors.confirm_password,
                        }"
                        v-model="password"
                        placeholder="Password*"
                        @input="onPasswordInput"
                      />
                      <span class="text-danger" v-if="fieldErrors.password">{{
                        fieldErrors.password[0]
                      }}</span>
                    </div>
                    <div class="form-group">
                      <input
                        type="password"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            fieldErrors.confirm_password || fieldErrors.password,
                        }"
                        v-model="confirm_password"
                        placeholder="Confirm Password*"
                        @input="onConfirmPasswordInput"
                      />
                      <span
                        class="text-danger"
                        v-if="fieldErrors.confirm_password"
                        >{{ fieldErrors.confirm_password[0] }}</span
                      >
                    </div>
                    <div class="d-flex align-items-start">
                      <input
                        type="checkbox"
                        class="btn-check signup-agreement-checkbox"
                      />
                      <p class="signup-agreement-text">
                        I agree to recieve automated promotional and
                        personalized text messages from Govava at the number
                        provided. Consent is not a condition of purchase.
                        Message frequency may vary. Reply STOP to opt out or
                        HELP for assistance. Msg & data rates may apply.
                      </p>
                    </div>
                    <div class="row ml-2" v-if="!recaptchaDisabled">
                      <turnstile-widget
                        ref="turnstile"
                        :site-key="turnstileSiteKey"
                        @verify="onVerify"
                        @error="errorMethod"
                        @expire="onTurnstileExpire"
                      />
                    </div>
                    <div class="row ml-2" v-if="!recaptchaDisabled">
                      <span class="text-danger" v-if="captchaError"
                        ><b>Please complete the security check</b></span
                      >
                    </div>
                    <span class="text-danger" v-if="fieldErrors.cfTurnstile">{{
                      fieldErrors.cfTurnstile[0]
                    }}</span>
                    <div class="row mb-1">
                      <div class="mt-4 mb-2 ml-3">
                        <button
                          @click="sendOtp"
                          type="button"
                          class="btn btn-primary btn-block px-4"
                          :disabled="!canCreateAccount"
                          :title="createAccountDisabledReason"
                        >
                          Create Account
                        </button>
                        <p
                          v-if="!canCreateAccount && createAccountDisabledReason"
                          class="text-danger mt-2 mb-0 signup-disabled-hint"
                        >
                          {{ createAccountDisabledReason }}
                        </p>
                      </div>
                    </div>
                    <p style="font-size: 14px">
                      Already have an account?
                      <router-link to="login" exact>Login</router-link>
                    </p>
                  </form>
                </div>
              </div>
            </div>
            <div class="col-md-7">
              <img
                src="@/assets/site/images/login-bg.jpg"
                alt=""
                class="img-fluid"
              />
            </div>
          </div>
        </div>
      </section>
      <b-modal
        v-model="otpModalVisible"
        id="sms-modal"
        centered
        title="Verify OTP"
        size="md"
        hide-footer
        :lazy="false"
        @hidden="onOtpModalHidden"
      >
        <section class="mx-3 signup-otp-section">
          <p>
            Enter OTP, It has been sent to your registered mobile number and
            email address.
          </p>
          <b-alert
            v-model="otpErrorVisible"
            variant="danger"
            dismissible
            class="mt-2 mb-3"
            @dismissed="otpError = null"
          >
            {{ otpError }}
          </b-alert>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': otpError }"
            v-model="otp"
            placeholder="Enter OTP"
            @input="clearOtpError"
            @keyup.enter="checkOtp"
          />
        </section>
        <div class="px-3 pb-3">
          <button
            type="button"
            class="btn btn-primary my-4 col-md-12"
            :disabled="verifyingOtp"
            @click="checkOtp"
          >
            {{ verifyingOtp ? "Verifying..." : "Verify" }}
          </button>
        </div>
      </b-modal>
    </b-overlay>
  </div>
</template>

<script>
import Vue from "vue";
import VueCountryCode from "vue-country-code";
import TurnstileWidget from "@/components/TurnstileWidget.vue";
import { TURNSTILE_SITE_KEY } from "@/utils/turnstile";
import { normalizeEmail } from "@/utils/emailValidation";
import { validateSignupForm } from "@/utils/signupValidation";
import { isRecaptchaDisabled } from "@/utils/recaptcha";
import emailFieldMixin from "@/mixins/emailFieldMixin";

Vue.use(VueCountryCode);

const JSON_HEADERS = {
  "content-type": "application/json",
  "X-Requested-With": "XMLHttpRequest",
};

export default {
  name: "SignupView",
  components: { TurnstileWidget },
  mixins: [emailFieldMixin],
  metaInfo: {
    title: "Signup",
    meta: [
      {
        name: "description",
        content:
          "Join GOVAVA by signing up for an account. Our user-friendly signup form ensures a seamless registration experience, with OTP verification and social media integration. Create your account today!",
      },
      {
        name: "keywords",
        content:
          "GOVAVA signup, create account, user registration, OTP verification, online forms, email signup, mobile registration, social media login",
      },
    ],
  },
  data() {
    return {
      name: null,
      gender: "",
      username: null,
      email: null,
      password: null,
      mobile: null,
      otp: null,
      login_mode: "Manual",
      unique_id: null,
      confirm_password: null,
      errors: [],
      loader: false,
      countrycode: "1",
      country: "US",
      response: null,
      captchaError: false,
      turnstileSiteKey: TURNSTILE_SITE_KEY,
      verifyingOtp: false,
      otpError: null,
      otpModalVisible: false,
      otpVerified: false,
      dismissOtpModalAfterSuccess: false,
      showValidationErrors: false,
    };
  },
  created() {
    this.setSocialUserData();
  },
  computed: {
    recaptchaDisabled() {
      return isRecaptchaDisabled();
    },
    signupFormState() {
      return validateSignupForm(this.getSignupFormValues(), {
        requireCaptcha: !this.recaptchaDisabled,
      });
    },
    hasSignupProgress() {
      return Boolean(
        String(this.username || "").trim() ||
          String(this.name || "").trim() ||
          String(this.email || "").trim() ||
          String(this.mobile || "").trim() ||
          this.password ||
          this.confirm_password
      );
    },
    fieldErrors() {
      const stateErrors = this.signupFormState.errors;
      const visibleErrors = {};

      if (this.errors.email) {
        visibleErrors.email = this.errors.email;
      } else if (this.showValidationErrors && stateErrors.email) {
        visibleErrors.email = stateErrors.email;
      }

      if (this.showValidationErrors || this.password) {
        if (stateErrors.password) {
          visibleErrors.password = stateErrors.password;
        }
      }

      if (
        this.confirm_password &&
        this.password &&
        this.password !== this.confirm_password
      ) {
        visibleErrors.confirm_password = [
          "The passwords you entered don't match. Please try again.",
        ];
      } else if (this.showValidationErrors || this.confirm_password) {
        if (stateErrors.confirm_password) {
          visibleErrors.confirm_password = stateErrors.confirm_password;
        }
      }

      if ((this.showValidationErrors || this.hasSignupProgress) && !this.gender) {
        visibleErrors.gender = stateErrors.gender || [
          "Please choose your gender to continue.",
        ];
      }

      if (this.showValidationErrors) {
        ["username", "name", "mobile", "cfTurnstile"].forEach((field) => {
          if (stateErrors[field]) {
            visibleErrors[field] = stateErrors[field];
          }
        });
        return {
          ...visibleErrors,
          ...this.errors,
        };
      }

      return visibleErrors;
    },
    createAccountDisabledReason() {
      if (this.canCreateAccount) {
        return "";
      }

      const priorityMessages = [
        this.fieldErrors.confirm_password &&
          this.fieldErrors.confirm_password[0],
        this.fieldErrors.password && this.fieldErrors.password[0],
        this.fieldErrors.gender && this.fieldErrors.gender[0],
        this.fieldErrors.email && this.fieldErrors.email[0],
        this.fieldErrors.cfTurnstile && this.fieldErrors.cfTurnstile[0],
        this.fieldErrors.mobile && this.fieldErrors.mobile[0],
        this.fieldErrors.username && this.fieldErrors.username[0],
        this.fieldErrors.name && this.fieldErrors.name[0],
      ].filter(Boolean);

      if (priorityMessages.length) {
        return priorityMessages[0];
      }

      if (this.emailDomainStatus === "checking") {
        return "We're verifying your email address...";
      }

      if (this.emailDomainStatus === "idle" && String(this.email || "").trim()) {
        return "Hang tight — we're still checking your email address.";
      }

      return "Please fill in all required fields marked with * to create your account.";
    },
    canCreateAccount() {
      if (!this.signupFormState.valid) {
        return false;
      }
      if (this.emailDomainStatus === "checking") {
        return false;
      }
      if (this.emailDomainStatus === "invalid") {
        return false;
      }
      if (this.emailDomainStatus === "idle") {
        return false;
      }
      return true;
    },
    otpErrorVisible: {
      get() {
        return Boolean(this.otpError);
      },
      set(value) {
        if (!value) {
          this.otpError = null;
        }
      },
    },
  },
  methods: {
    isApiSuccess(status) {
      return status === true || status === 1 || status === "1";
    },
    isApiFailure(status) {
      return status === false || status === 0 || status === "0";
    },
    clearOtpError() {
      this.otpError = null;
    },
    showOtpModalError(message) {
      this.otpError = message;
      this.otpModalVisible = true;
    },
    getSignupFormValues() {
      return {
        username: this.username,
        name: this.name,
        email: this.email,
        mobile: this.mobile,
        countrycode: this.countrycode,
        gender: this.gender,
        password: this.password,
        confirm_password: this.confirm_password,
        response: this.response,
      };
    },
    apiUrl(path) {
      return process.env.VUE_APP_URL + path;
    },
    getSignupPayload(extra = {}) {
      return {
        name: this.name,
        username: this.username,
        gender: this.gender,
        email: normalizeEmail(this.email),
        mobile: this.mobile,
        unique_id: this.unique_id,
        login_mode: this.login_mode,
        password: this.password,
        confirm_password: this.confirm_password,
        country_code: this.countrycode,
        country: this.country,
        ...extra,
      };
    },
    postJson(path, body) {
      return fetch(this.apiUrl(path), {
        method: "post",
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
      }).then((res) =>
        res.json().then((data) => ({
          ok: res.ok,
          data,
        }))
      );
    },
    setSocialUserData() {
      const socialDataRaw = localStorage.getItem("socialData");
      if (!socialDataRaw) {
        return;
      }

      const socialData = JSON.parse(socialDataRaw);
      const socialUser = socialData.user;
      this.username = socialUser.name;
      this.name = socialUser.name;
      this.email = socialUser.email ? normalizeEmail(socialUser.email) : null;
      this.unique_id = socialUser.id;
      this.login_mode = socialData.provider;
      if (this.email) {
        this.scheduleEmailDomainCheck();
      }
    },
    onVerify(response) {
      this.response = response;
      this.captchaError = false;
    },
    onTurnstileExpire() {
      this.response = null;
    },
    resetTurnstile() {
      this.response = null;
      if (this.$refs.turnstile) {
        this.$refs.turnstile.reset();
      }
    },
    errorMethod(response) {
      this.response = null;
      console.log("Turnstile error >>>>>  ", response);
    },
    onSelect({ iso2, dialCode }) {
      this.countrycode = dialCode;
      this.country = iso2;
    },
    onPasswordInput() {
      if (this.errors.password) {
        this.$delete(this.errors, "password");
      }
    },
    onConfirmPasswordInput() {
      if (this.errors.confirm_password) {
        this.$delete(this.errors, "confirm_password");
      }
    },
    sendOtp() {
      this.showValidationErrors = true;
      const validation = validateSignupForm(this.getSignupFormValues(), {
        requireCaptcha: !this.recaptchaDisabled,
      });
      this.errors = { ...validation.errors };

      if (!validation.valid) {
        return;
      }

      if (this.emailDomainStatus === "idle") {
        this.runEmailDomainCheck();
        return;
      }

      if (this.emailDomainStatus === "checking") {
        return;
      }

      if (this.emailDomainStatus === "invalid") {
        return;
      }

      this.email = validation.normalizedEmail;
      this.postJson("customer/sendOtpWeb", {
        ...this.getSignupPayload(),
        captchaError: false,
        cfTurnstile: this.response,
      })
        .then(({ data }) => {
          if (this.isApiSuccess(data.status)) {
            this.otp = null;
            this.clearOtpError();
            this.otpVerified = false;
            this.otpModalVisible = true;
            if (data.debug_otp) {
              this.$swal(
                "Local dev OTP",
                "Your verification code is: " + data.debug_otp,
                "info"
              );
            }
          } else if (this.isApiFailure(data.status)) {
            this.$swal("Error", data.message, "error");
          } else if (data.errors) {
            this.errors = data.errors;
          }
        })
        .catch((err) => {
          console.log("Register Data", "Error : " + err.message, "error");
        });
    },
    onOtpModalHidden() {
      if (this.dismissOtpModalAfterSuccess) {
        this.dismissOtpModalAfterSuccess = false;
        return;
      }
      this.resetOtpModal();
    },
    resetOtpModal() {
      this.otp = null;
      this.clearOtpError();
      this.verifyingOtp = false;
      this.otpVerified = false;
    },
    checkOtp() {
      const otpValue = (this.otp || "").trim();
      if (!otpValue) {
        this.showOtpModalError("Please enter the OTP.");
        return;
      }

      this.clearOtpError();
      this.errors = [];
      this.otpVerified = false;
      this.verifyingOtp = true;
      this.postJson(
        "customer/verifyotpPin",
        this.getSignupPayload({ otp: otpValue })
      )
        .then(({ data }) => {
          if (this.isApiSuccess(data.status)) {
            this.otpVerified = true;
            this.dismissOtpModalAfterSuccess = true;
            this.otpModalVisible = false;
            this.register();
            return;
          }

          this.otpVerified = false;
          this.showOtpModalError(
            data.message || "Invalid OTP. Please check and try again."
          );
          if (data.errors) {
            this.errors = data.errors;
          }
        })
        .catch((err) => {
          console.log("Register Data", "Error : " + err.message, "error");
          this.otpVerified = false;
          this.showOtpModalError("Could not verify OTP. Please try again.");
        })
        .finally(() => {
          this.verifyingOtp = false;
        });
    },
    register() {
      if (!this.otpVerified) {
        return;
      }

      this.errors = [];
      this.loader = true;
      this.postJson("customer/customerRegisterWeb", this.getSignupPayload())
        .then(({ data }) => {
          if (this.isApiSuccess(data.status)) {
            const userData = {
              user: data.customer,
              token: data.token,
              status: true,
            };
            localStorage.setItem("userData", JSON.stringify(userData));
            window.location.href = "home";
            return;
          }

          this.loader = false;
          this.otpVerified = false;

          const message = data.message || "Registration failed. Please try again.";
          if (/otp/i.test(message)) {
            this.showOtpModalError(message);
            return;
          }

          if (this.isApiFailure(data.status)) {
            this.$swal("Error", message, "error");
          } else if (data.errors) {
            this.errors = data.errors;
          }
        })
        .catch((err) => {
          this.loader = false;
          this.otpVerified = false;
          console.log("Register Data", "Error : " + err.message, "error");
        });
    },
    resetForm() {
      this.name = null;
      this.username = null;
      this.gender = null;
      this.email = null;
      this.password = null;
      this.mobile = null;
      this.confirm_password = null;
      this.errors = [];
      this.response = null;
      this.captchaError = false;
      this.resetTurnstile();
    },
  },
};
</script>

<style>
.signup-agreement-checkbox {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.signup-agreement-text {
  color: #000000 !important;
  font-size: 16px !important;
  line-height: 1.5;
  font-weight: 500;
}

.signup-otp-section {
  max-height: 280px;
}

.signup-field-hint {
  font-size: 13px;
}

.signup-disabled-hint {
  font-size: 13px;
  line-height: 1.4;
}

.btn-primary:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
</style>
